import { withSlash } from 'lib/dealhunt/url'
import { HIDDEN_MEMBER_NAME, replyAuthorOf } from './discussions'
import type { DiscussionIdentityOfThread } from './discussions-types'

/**
 * Reply authors DealHunt has not mirrored yet (owner-notes contract #213, 2026-10-18).
 *
 * A thread's `identities` map (review and discussion threads alike) is built from DealHunt's
 * MIRRORED reply log, ~70 s behind commentHub. A member's fresh reply is absent from it, and
 * falling through to commentHub's name printed their real (Google) name even when they keep the
 * pseudonym. So the thread page asks DealHunt's bulk read for the missing hashes and, until it
 * answers — or when it fails — draws the neutral "Member" label, never commentHub's name.
 *
 * Three states per hash (`LiveIdentities`): absent = not answered yet (or the read failed) →
 * "Member"; `null` = DealHunt omitted it (no claimed profile) → commentHub's own; an entry →
 * DealHunt's rule (`replyAuthorOf`; a pseudonymous member comes back `hidden` with no handle →
 * "Member").
 */

/** DealHunt's cap per call (`OWNER_COMMUNITY_IDENTITIES_MAX_HASHES`). */
export const IDENTITIES_MAX_HASHES = 50

/** commentHub's `authorSubjectHash`: lowercase hex sha256. */
const SUBJECT_HASH_RE = /^[0-9a-f]{64}$/

/** The hash in its one spelling, or null when it cannot be a subject hash. */
export function subjectHash(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const hash = value.trim().toLowerCase()
  return SUBJECT_HASH_RE.test(hash) ? hash : null
}

/**
 * The proxy's `hashes` query: a comma list of 1–50 distinct subject hashes, lowercased, deduped
 * and SORTED (every spelling of one set is one cache entry). Strict like DealHunt (#213): any
 * malformed entry, the array form, none or more than 50 → null (400) — a dropped hash would read
 * "omitted" and draw commentHub's name.
 */
export function identitiesHashesParam(raw: unknown): string[] | null {
  if (typeof raw !== 'string' || raw === '') {
    return null
  }
  const out = new Set<string>()
  for (const part of raw.split(',')) {
    const hash = subjectHash(part)
    if (!hash) {
      return null
    }
    out.add(hash)
  }
  return out.size > 0 && out.size <= IDENTITIES_MAX_HASHES ? [...out].sort() : null
}

export function identitiesApiPath(hashes: string[], trailingSlash: boolean): string {
  return `${withSlash('/api/community/identities', trailingSlash)}?hashes=${hashes.join(',')}`
}

/** DealHunt's neutral entry: membership only, no handle — drawn "Member". */
const NEUTRAL_IDENTITY: DiscussionIdentityOfThread = {
  kind: 'hidden',
  handle: null,
  handle_display: null,
}

function identityEntry(value: unknown): DiscussionIdentityOfThread | null {
  const v = value as Record<string, unknown> | null
  if (!v || typeof v !== 'object') {
    return null
  }
  const str = (x: unknown): string | null => (typeof x === 'string' && x ? x : null)
  if (v.kind === 'shown' && str(v.name) && str(v.handle)) {
    return {
      kind: 'shown',
      name: v.name as string,
      avatar_url: str(v.avatar_url),
      handle: v.handle as string,
      handle_display: str(v.handle_display) ?? (v.handle as string),
    }
  }
  if (v.kind === 'hidden') {
    return { kind: 'hidden', handle: str(v.handle), handle_display: str(v.handle_display) }
  }
  return null
}

/**
 * DealHunt's answer kept to the requested hashes: each requested hash maps to its entry, or to
 * `null` when DealHunt omitted it (no claimed profile). An entry of an unknown shape becomes the
 * neutral `hidden` entry ("Member") — a member, never omitted (that would print commentHub's name).
 * Null when the body is not the contract's.
 */
export function identitiesProjection(
  body: unknown,
  requested: ReadonlyArray<string>
): LiveIdentities | null {
  const raw = (body as { identities?: unknown } | null)?.identities
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }
  const map = raw as Record<string, unknown>
  const out: LiveIdentities = {}
  requested.forEach(hash => {
    if (!Object.prototype.hasOwnProperty.call(map, hash)) {
      out[hash] = null
      return
    }
    out[hash] = identityEntry(map[hash]) ?? NEUTRAL_IDENTITY
  })
  return out
}

/** Hash → DealHunt's entry, or null = omitted (a non-member). A hash not in it is unresolved. */
export type LiveIdentities = Record<string, DiscussionIdentityOfThread | null>

/** The neutral author of an unresolved hash: no name, no photo (the widget's initials of it). */
export const UNRESOLVED_AUTHOR = { name: HIDDEN_MEMBER_NAME, avatarUrl: null } as const

/**
 * The frame's `author` answer for one live reply. The thread's own map wins (it carries a
 * participant's handle); then the bulk read's answer; a hash nobody has answered for yet is the
 * neutral "Member". No hash at all → commentHub's own (nothing to ask DealHunt).
 */
export function liveReplyAuthor(
  hash: string | null | undefined,
  thread: Record<string, DiscussionIdentityOfThread> | null | undefined,
  live: LiveIdentities
): { name: string; avatarUrl: string | null } | null {
  const fromThread = replyAuthorOf(hash, thread)
  if (fromThread) {
    return fromThread
  }
  const key = subjectHash(hash)
  if (!key) {
    return null
  }
  if (!Object.prototype.hasOwnProperty.call(live, key)) {
    return UNRESOLVED_AUTHOR
  }
  const entry = live[key]
  return entry ? replyAuthorOf(key, { [key]: entry }) : null
}

// Browser memo: one answer per sorted hash set for a minute (DealHunt's max-age).
const MEMO_MS = 60_000
const memo = new Map<string, { at: number; value: Promise<LiveIdentities | null> }>()

/**
 * One batched read (≤ 50 hashes) through SoundGuys' proxy. Null on any failure (404 before
 * DealHunt ships the route, 400, 5xx, network) — the caller keeps "Member" for those hashes.
 */
export function fetchLiveIdentities(
  hashes: string[],
  trailingSlash: boolean,
  now: number = Date.now()
): Promise<LiveIdentities | null> {
  const sorted = [...new Set(hashes)].sort().slice(0, IDENTITIES_MAX_HASHES)
  const key = sorted.join(',')
  const hit = memo.get(key)
  if (hit && now - hit.at < MEMO_MS) {
    return hit.value
  }
  const value = fetch(identitiesApiPath(sorted, trailingSlash), {
    headers: { Accept: 'application/json' },
  })
    .then(res => (res.ok ? res.json() : null))
    .then((body: unknown) => (body ? identitiesProjection(body, sorted) : null))
    .catch(() => null)
    .then(result => {
      if (result === null) {
        memo.delete(key) // a failure is not remembered: the next page asks again
      }
      return result
    })
  memo.set(key, { at: now, value })
  return value
}

/** Tests only. */
export function clearLiveIdentitiesMemo(): void {
  memo.clear()
}
